/* eslint-disable max-len */
/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2025 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * Status Tag Menu Component
 *
 * @file StatusTagMenu.jsx
 * @author Murhsid Ahamedh
 */

import React from 'react';
import PropTypes from 'prop-types';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import Box from '@mui/material/Box';
import DropdownMenu from './DropdownMenu';
import { PERFORMANCE_STATUS } from '../../common/constants';
import { getBackgroundColor, getTextColor } from '../../common/statusColorUtils';
import mixins from '../../assets/styles/mixins.scss';


const TAG_STYLES = {
  Great: { backgroundColor: getBackgroundColor(PERFORMANCE_STATUS.GREAT), color: getTextColor(PERFORMANCE_STATUS.GREAT) },
  Okay: { backgroundColor: getBackgroundColor(PERFORMANCE_STATUS.OKAY), color: getTextColor(PERFORMANCE_STATUS.OKAY) },
  'Needs review': { backgroundColor: getBackgroundColor(PERFORMANCE_STATUS.NEEDS_REVIEW), color: getTextColor(PERFORMANCE_STATUS.NEEDS_REVIEW) },
  'Needs work': { backgroundColor: getBackgroundColor(PERFORMANCE_STATUS.NEEDS_WORK), color: getTextColor(PERFORMANCE_STATUS.NEEDS_WORK) }
};

// Same box and type as the Assignments tab status tag (assignmentListItemStyles statusTag)
const TAG_BASE_STYLE = {
  display: 'inline-flex',
  alignItems: 'center',
  height: 20,
  padding: '0 6px 2px',
  borderRadius: 4,
  boxSizing: 'border-box',
  fontFamily: mixins.fontFamilyPlusSans,
  fontSize: 12,
  fontWeight: mixins.fontWeight500,
  lineHeight: '20px',
  whiteSpace: 'nowrap'
};

const TagPill = ({ label, width }) => {
  const { backgroundColor, color } = TAG_STYLES[label] || { backgroundColor: mixins.nameLabelBgColor, color: mixins.timeIconColor };

  return (
    <Box style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <Box
        style={{
          ...TAG_BASE_STYLE,
          width,
          backgroundColor,
          color,
          marginLeft: 8
        }}
      >
        <Box component="span" style={{ color }}>{label}</Box>
      </Box>
      <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <ArrowDropDownIcon style={{ color: mixins.blackColor, fontSize: 18 }} />
      </Box>
    </Box>
  );
};

TagPill.propTypes = {
  label: PropTypes.string.isRequired,
  // eslint-disable-next-line react/require-default-props
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

const StatusTagMenu = ({ selectedTag, onSelect, frameDoc, sections }) => {
  const handleTagSelection = (id, label) => {
    if (onSelect) onSelect(id, label);
  };

  const tagItems = sections?.map((item) => ({
    ...item,
    onClick: () => handleTagSelection(item.id, item.label)
  }));
  const renderTagItem = (item, isHovered, closeDropdown) => {
    const styles = TAG_STYLES[item.label] || { backgroundColor: mixins.nameLabelBgColor, color: mixins.timeIconColor };
    const isSelected = item.label === selectedTag;

    return (
      <div
        role="button"
        tabIndex={0}
        onMouseDown={(e) => { e.preventDefault(); handleTagSelection(item.id, item.label); if (closeDropdown) closeDropdown(); }}
        style={{
          cursor: 'pointer',
          backgroundColor: (isHovered || isSelected) ? mixins.lightThemeBgColor2 : 'transparent',
          padding: '8px 12px',
          minHeight: 36,
          display: 'flex',
          alignItems: 'center',
          borderRadius: 0,
          width: '100%',
          boxSizing: 'border-box'
        }}
      >
        <Box
          style={{
            ...TAG_BASE_STYLE,
            backgroundColor: styles.backgroundColor,
            color: styles.color
          }}
        >
          <Box component="span" style={{ color: styles.color }}>{item.label}</Box>
        </Box>
      </div>
    );
  };

  return (
    <DropdownMenu
      items={tagItems}
      triggerAriaLabel="Select feedback tag"
      dropdownStyle={{ border: '1px solid #6F3FF5', borderRadius: 4, minWidth: 200 }}
      menuItemStyle={{ padding: 0 }}
      triggerStyle={{ justifyContent: 'flex-end' }}
      triggerHoverStyle={{ backgroundColor: 'transparent' }}
      triggerContent={<TagPill label={selectedTag} />}
      renderItem={renderTagItem}
      frameDoc={frameDoc}
    />
  );
};

StatusTagMenu.propTypes = {
  selectedTag: PropTypes.string.isRequired,
  onSelect: PropTypes.func,
  frameDoc: PropTypes.object,
  sections: PropTypes.array
};

StatusTagMenu.defaultProps = {
  onSelect: undefined,
  frameDoc: null,
  sections: []
};

export default StatusTagMenu;

